import { format, isValid } from "date-fns";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { NextSeo } from "next-seo";

import { ContentRender } from "@/features/blog/components/ContentRenderer";
import { SideBlogList } from "@/features/blog/components/SideBlogList";
import { notionBlogDbIds } from "@/features/blog/consts/notionBlogDbIds";
import { blogPageMapper } from "@/features/blog/helpers/blogPageMapper";
import { BlogPost } from "@/features/blog/type";
import { Breadcrumbs } from "@/features/common/components/Breadcrumbs";
import { Container } from "@/features/common/components/Container";
import { Title } from "@/features/common/components/Title";
import { useSeoProps } from "@/features/common/hooks/useSeoProps";
import { SiteLayout } from "@/features/layout/SiteLayout";
import { getPublicConfig } from "@/helpers/getPublicConfig";
import { notionClient } from "@/services/notion/client";
import { dbQuery } from "@/services/notion/dbQuery";
import { notionGetPage } from "@/services/notion/getPage";

const config = getPublicConfig();

function BlogPostPage(
  props: InferGetServerSidePropsType<typeof getServerSideProps>,
) {
  const { recordMap, blogPage, blogPosts } = props;
  const { coverImageUrl, title, description, displayDate, id } = blogPage;

  const seoProps = useSeoProps({ title, description });
  const date = new Date(displayDate);

  return (
    <Container className="flex space-x-8 py-8 sm:pt-12 sm:pb-24">
      <NextSeo {...seoProps} />

      <div className="flex flex-col items-start space-y-4">
        <div className="py-2">
          <Breadcrumbs
            items={[
              { title: "首頁", href: "/" },
              { title: "網誌", href: "/blog" },
              {
                title: title,
                href: `/blog/${id}/${title}`,
              },
            ]}
          />
        </div>

        <Title size="h2" className="font-normal text-gray-900">
          {title}
        </Title>

        {coverImageUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            className="aspect-[3/2] object-cover"
            alt={title}
            width={900}
            height={600}
            src={coverImageUrl}
          />
        )}

        <div className="text-sm text-gray-400">
          KOL Jobs |{" "}
          {isValid(date) ? format(date, config.features.common.dateFormat) : ""}
        </div>

        <div style={{ maxWidth: "calc(100vw - 24px)" }}>
          <ContentRender recordMap={recordMap} />
        </div>
      </div>

      <div className="min-x-[480px] hidden xl:flex">
        <SideBlogList blogPosts={blogPosts} />
      </div>
    </Container>
  );
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext<Record<"pageId" | "pageSlug", string>>,
) => {
  const blogDbId = config.env.isProduction
    ? notionBlogDbIds.production
    : notionBlogDbIds.preview;

  const [recordMap, blogPosts, blogPage] = await Promise.all([
    notionClient.getPage(context.params.pageId),
    dbQuery(blogDbId, {
      filter: {
        property: "status",
        select: { equals: "Public" },
      },
      sorts: [{ property: "Display Date", direction: "descending" }],
      mapper: blogPageMapper,
    }),
    notionGetPage({
      page_id: context.params.pageId,
      mapper: blogPageMapper,
    }),
  ]);

  return {
    props: {
      recordMap,
      blogPage: blogPage as BlogPost,
      blogPosts: blogPosts as BlogPost[],
    },
  };
};

export default BlogPostPage;

BlogPostPage.getLayout = function getLayout(page) {
  return <SiteLayout>{page}</SiteLayout>;
};
